/**
 * SessionGuard Component
 *
 * Purpose: Guards invoice management routes by checking for active payment session.
 * Renders SessionRequiredPage inline if no session exists (keeps MainLayout intact).
 *
 * Usage: Wrap routes that require an active payment session
 *
 * @example
 * <SessionGuard>
 *   <StudentFeesPage />
 * </SessionGuard>
 */

import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/stateHooks';
import { getActiveSession } from '../redux/action/payment.action';
import SessionRequiredPage from '../pages/sessionRequired';

interface SessionGuardProps {
  children: React.ReactNode;
}

const SessionGuard: React.FC<SessionGuardProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { activeSession, loadingActive } = useAppSelector((state) => state.paymentSession);
  const [isInitialCheck, setIsInitialCheck] = useState(true);

  // Check for active session on mount
  useEffect(() => {
    dispatch(getActiveSession()).finally(() => {
      setIsInitialCheck(false);
    });
  }, [dispatch]);

  // Show loading spinner while checking session (initial check or subsequent loads)
  if (isInitialCheck || loadingActive) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <i className="pi pi-spinner pi-spin text-4xl text-purple-600" />
      </div>
    );
  }

  // If no session exists, render SessionRequiredPage inline (within MainLayout)
  if (!activeSession) {
    return <SessionRequiredPage />;
  }

  // Session exists - render protected children
  return <>{children}</>;
};

export default SessionGuard;
